import { useEffect, useMemo, useState } from "react";
import { api, apiUrl, fmtINR, fmtPeriod } from "@/lib/api";
import { PageHeader, Section, Btn, Modal, Input, Select, ComboSelect, Badge, MonthPicker } from "@/components/Shared";
import { Plus, Pencil, Trash2, Download, ArrowUp, ArrowDown, ArrowUpDown, Wallet, X, CalendarRange } from "lucide-react";
import { toast } from "sonner";
import { openConfirm } from "@/lib/dialogs";
import { INDIA_STATES, STATE_NAME_BY_CODE, locationPill, normaliseLocation } from "@/lib/indiaStates";

const EMPTY = { name: "", role: "employee", for_from: "", pan: "", gstin: "", tag: "", salary_per_month: 0, account_number: "", ifsc: "", location: "", total_to_pay: 0, total_paid: 0, comments: "" };
const EMPTY_LEDGER = { month: "", amount_due: 0, amount_paid: 0, notes: "" };

const STATE_OPTIONS = [{ value: "", label: "—" }, ...INDIA_STATES.map((s) => ({ value: s.code, label: `${s.code} · ${s.name}` }))];

function StatePill({ value }) {
  const p = locationPill(value);
  if (!p) return <span className="text-[var(--muted)]">—</span>;
  return (
    <span
      title={p.label}
      className={`inline-flex items-center px-1.5 py-0.5 text-[11px] font-bold uppercase tracking-wider border rounded-sm ${
        p.known ? "bg-[var(--surface-2)] text-[var(--ink)] border-[var(--line-2)]" : "bg-amber-50 text-amber-800 border-amber-200"
      }`}
      data-testid={`state-pill-${p.code}`}
    >
      {p.code}
    </span>
  );
}

export default function People() {
  const [rows, setRows] = useState([]);
  const [forFromOpts, setForFromOpts] = useState([]);
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [locFilter, setLocFilter] = useState("");
  const [tagFilter, setTagFilter] = useState("");
  const [sortBy, setSortBy] = useState({ key: "name", dir: "asc" });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);

  // Salary drawer
  const [drawerPerson, setDrawerPerson] = useState(null);
  const [ledger, setLedger] = useState({ rows: [], rollup: [] });
  const [ledgerOpen, setLedgerOpen] = useState(false);
  const [ledgerForm, setLedgerForm] = useState(EMPTY_LEDGER);
  const [ledgerEditId, setLedgerEditId] = useState(null);

  // Bulk salary run
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkMonth, setBulkMonth] = useState("");
  const [bulkRows, setBulkRows] = useState([]);  // [{person_id, name, for_from, role, salary_per_month, selected, amount_due, amount_paid, notes}]
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = async () => {
    const [p, ff] = await Promise.all([api.get("/people"), api.get("/people/for-from-options")]);
    setRows(p.data || []);
    setForFromOpts(ff.data || []);
  };
  useEffect(() => { load(); }, []);

  const loadLedger = async (personId) => {
    const r = await api.get(`/people/${personId}/salary-ledger`);
    setLedger(r.data);
  };
  const openDrawer = async (person) => {
    setDrawerPerson(person);
    await loadLedger(person.id);
  };
  const closeDrawer = () => { setDrawerPerson(null); setLedger({ rows: [], rollup: [] }); };

  const saveLedger = async () => {
    try {
      const p = { ...ledgerForm, person_id: drawerPerson.id, amount_paid: Number(ledgerForm.amount_paid || 0), amount_due: Number(ledgerForm.amount_due || 0) };
      if (ledgerEditId) await api.put(`/people/${drawerPerson.id}/salary-ledger/${ledgerEditId}`, p);
      else await api.post(`/people/${drawerPerson.id}/salary-ledger`, p);
      toast.success("Saved"); setLedgerOpen(false); setLedgerForm(EMPTY_LEDGER); setLedgerEditId(null);
      await loadLedger(drawerPerson.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const removeLedger = async (id) => {
    if (!await openConfirm({ title: "Delete ledger entry?", message: "This salary disbursement entry will be removed from the person's ledger.", confirmLabel: "Delete entry", danger: true })) return;
    await api.delete(`/people/${drawerPerson.id}/salary-ledger/${id}`); toast.success("Deleted"); await loadLedger(drawerPerson.id);
  };
  const editLedger = (r) => { setLedgerForm({ ...r }); setLedgerEditId(r.id); setLedgerOpen(true); };
  const openNewLedger = () => {
    // CTC is annual — the per-month run-rate (default Paid) is CTC / 12.
    // The locked CTC field shows the annual figure for clarity.
    const ctcAnnual = drawerPerson.salary_per_month || 0;
    const monthlyRunRate = Math.round((ctcAnnual || 0) / 12);
    setLedgerForm({ ...EMPTY_LEDGER, amount_due: ctcAnnual, amount_paid: monthlyRunRate });
    setLedgerEditId(null);
    setLedgerOpen(true);
  };

  // Bulk salary run helpers
  const openBulk = () => {
    const eligible = rows.filter((p) => Number(p.salary_per_month || 0) > 0);
    setBulkRows(eligible.map((p) => {
      const ctcAnnual = Number(p.salary_per_month || 0);
      const monthlyRunRate = Math.round(ctcAnnual / 12);
      const fyPending = Number(p.fy_pending || 0);
      return {
        person_id: p.id,
        name: p.name,
        for_from: p.for_from || "",
        role: p.role,
        tag: p.tag || "",
        salary_per_month: ctcAnnual,  // alias kept for sort/filter compat
        ctc_annual: ctcAnnual,
        ctc_monthly: monthlyRunRate,
        fy_pending: fyPending,  // FY pending = CTC − Paid (already from /people)
        fy_label: p.fy_label || "FY",
        selected: true,
        amount_due: ctcAnnual,        // CTC locked — display only (annual)
        amount_paid: monthlyRunRate,  // default Paid = monthly run-rate
        notes: "",
      };
    }));
    setBulkMonth("");
    setBulkOpen(true);
  };
  const updateBulkRow = (pid, field, val) => {
    setBulkRows((rs) => rs.map((r) => r.person_id === pid ? { ...r, [field]: val } : r));
  };
  const toggleAllBulk = (selected) => setBulkRows((rs) => rs.map((r) => ({ ...r, selected })));
  // Select-by-tag helper — toggles only rows matching the given tag ("NK"/"RR"/null=untagged).
  // Other rows are deselected so the action is "show me only NK / RR / untagged" not "additive".
  // This lets admin payroll just one cohort at a time without manually clicking each checkbox.
  const selectByTag = (tag) => setBulkRows((rs) => rs.map((r) => {
    const matches = tag === null ? !r.tag : r.tag === tag;
    return { ...r, selected: matches };
  }));
  const resetToSalary = () => setBulkRows((rs) => rs.map((r) => ({
    ...r,
    amount_due: r.ctc_annual,
    amount_paid: r.ctc_monthly,  // reset to monthly run-rate, NOT annual
  })));
  const copyLastMonth = async () => {
    if (!bulkMonth) { toast.error("Pick a month first"); return; }
    try {
      const res = await api.get(`/people/salary-ledger/last-month?before_month=${bulkMonth}`);
      const byPerson = Object.fromEntries((res.data.entries || []).map((e) => [e.person_id, e]));
      let matched = 0;
      setBulkRows((rs) => rs.map((r) => {
        const prev = byPerson[r.person_id];
        if (prev) {
          matched += 1;
          return {
            ...r,
            amount_due: Number(prev.amount_due || 0),
            amount_paid: Number(prev.amount_paid || 0),
            notes: prev.notes || "",
          };
        }
        return r;
      }));
      toast.success(`Copied ${matched} row${matched === 1 ? "" : "s"} from the latest prior month`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const saveBulk = async () => {
    if (!bulkMonth) { toast.error("Pick a month"); return; }
    const entries = bulkRows.filter((r) => r.selected).map((r) => ({
      person_id: r.person_id,
      amount_due: Number(r.amount_due || 0),
      amount_paid: Number(r.amount_paid || 0),
      notes: r.notes || "",
    }));
    if (!entries.length) { toast.error("Select at least one person"); return; }
    setBulkBusy(true);
    try {
      const res = await api.post("/people/salary-ledger/bulk", { month: bulkMonth, entries, skip_existing: true });
      const ins = res.data.inserted;
      const sk = res.data.skipped;
      toast.success(`${ins} entr${ins === 1 ? "y" : "ies"} added${sk ? ` · ${sk} skipped (already exists)` : ""}`);
      setBulkOpen(false);
      if (drawerPerson) await loadLedger(drawerPerson.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setBulkBusy(false);
  };

  const save = async () => {
    try {
      const p = { ...form, salary_per_month: Number(form.salary_per_month || 0), total_to_pay: Number(form.total_to_pay || 0), total_paid: Number(form.total_paid || 0) };
      if (editId) await api.put(`/people/${editId}`, p); else await api.post("/people", p);
      toast.success("Saved"); setOpen(false); setForm(EMPTY); setEditId(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const remove = async (id) => {
    if (!await openConfirm({ title: "Delete person?", message: "This person and their salary ledger history will be removed. You can't undo this.", confirmLabel: "Delete person", danger: true })) return;
    await api.delete(`/people/${id}`); toast.success("Deleted"); load();
  };
  const openEdit = (r) => { setForm({ ...r }); setEditId(r.id); setOpen(true); };
  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };

  const toggleSort = (key) => setSortBy((s) => s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" });
  const SortHeader = ({ k, children, align = "left" }) => {
    const active = sortBy.key === k;
    const Icon = !active ? ArrowUpDown : (sortBy.dir === "asc" ? ArrowUp : ArrowDown);
    return (
      <th className={align === "right" ? "text-right" : ""}>
        <button type="button" onClick={() => toggleSort(k)}
          className={`inline-flex items-center gap-1 hover:text-[var(--ink)] ${active ? "text-[var(--ink)]" : ""} ${align === "right" ? "flex-row-reverse" : ""}`}>
          <span>{children}</span>
          <Icon size={11} strokeWidth={2} className={active ? "text-[var(--accent)]" : "text-[var(--muted)]"} />
        </button>
      </th>
    );
  };

  const locOptions = useMemo(() => {
    // Provide both: known state codes + any unique legacy free-text locations
    // already on records (so the filter dropdown still surfaces them).
    const present = new Set(rows.map((r) => normaliseLocation(r.location)).filter(Boolean));
    return [...present].sort();
  }, [rows]);
  const filtered = useMemo(() => {
    let list = rows.filter((r) => {
      if (roleFilter && r.role !== roleFilter) return false;
      if (locFilter && normaliseLocation(r.location) !== locFilter) return false;
      if (tagFilter === "untagged") { if (r.tag) return false; }
      else if (tagFilter && (r.tag || "") !== tagFilter) return false;
      if (q && !`${r.name} ${r.pan} ${r.gstin} ${r.for_from} ${r.account_number}`.toLowerCase().includes(q.toLowerCase())) return false;
      return true;
    });
    const { key, dir } = sortBy;
    const mul = dir === "asc" ? 1 : -1;
    list = [...list].sort((a, b) => {
      const va = a[key], vb = b[key];
      if (typeof va === "number") return (va - vb) * mul;
      return String(va || "").localeCompare(String(vb || "")) * mul;
    });
    return list;
  }, [rows, q, roleFilter, locFilter, tagFilter, sortBy]);

  const empCount = rows.filter((r) => r.role === "employee").length;
  const vendCount = rows.filter((r) => r.role === "vendor").length;
  const salariedCount = rows.filter((r) => Number(r.salary_per_month || 0) > 0).length;
  const nkCount = rows.filter((r) => r.tag === "NK").length;
  const rrCount = rows.filter((r) => r.tag === "RR").length;
  const hasFilters = q || roleFilter || locFilter || tagFilter;
  const bulkSelectedCount = bulkRows.filter((r) => r.selected).length;
  const bulkTotalDue = bulkRows.filter((r) => r.selected).reduce((a, r) => a + Number(r.amount_due || 0), 0);
  const bulkTotalPaid = bulkRows.filter((r) => r.selected).reduce((a, r) => a + Number(r.amount_paid || 0), 0);

  return (
    <div data-testid="people-page">
      <PageHeader title="People" actions={<>
        <Btn variant="ghost" onClick={() => window.open(apiUrl(`/export/people${roleFilter ? `?role=${roleFilter}` : ""}`), "_blank")}><Download size={14} /> People CSV</Btn>
        <Btn variant="ghost" testid="people-export-ledger" onClick={() => window.open(apiUrl(`/export/salary-ledger${tagFilter && tagFilter !== "untagged" ? `?tag=${tagFilter}` : ""}`), "_blank")}><Download size={14} /> Ledger CSV{tagFilter && tagFilter !== "untagged" ? ` · ${tagFilter}` : ""}</Btn>
        <Btn variant="ghost" testid="people-run-salaries" onClick={openBulk}><CalendarRange size={14} /> Run Monthly Salaries</Btn>
        <Btn variant="accent" testid="people-add" onClick={openNew}><Plus size={14} /> New Person</Btn>
      </>} />
      <div className="p-4 sm:p-10 space-y-5">
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          <div className="bg-[var(--surface)] border border-[var(--line)] p-4"><div className="text-[10px] uppercase tracking-[0.2em] text-[var(--muted)] font-semibold">Total People</div><div className="num text-xl font-semibold mt-1.5">{rows.length}</div></div>
          <div className="bg-[var(--surface)] border border-[var(--line)] p-4"><div className="text-[10px] uppercase tracking-[0.2em] text-[var(--muted)] font-semibold">Employees</div><div className="num text-xl font-semibold mt-1.5">{empCount}</div></div>
          <div className="bg-[var(--surface)] border border-[var(--line)] p-4"><div className="text-[10px] uppercase tracking-[0.2em] text-[var(--muted)] font-semibold">Vendors</div><div className="num text-xl font-semibold mt-1.5">{vendCount}</div></div>
          <div className="bg-[var(--surface)] border border-[var(--line)] p-4"><div className="text-[10px] uppercase tracking-[0.2em] text-[var(--muted)] font-semibold">With monthly salary</div><div className="num text-xl font-semibold mt-1.5 text-[var(--accent)]">{salariedCount}</div><div className="text-[10px] text-[var(--muted)] mt-1">eligible for bulk run</div></div>
          <div className="bg-[var(--surface)] border border-[var(--line)] p-4"><div className="text-[10px] uppercase tracking-[0.2em] text-[var(--muted)] font-semibold">Tagged NK / RR</div><div className="num text-xl font-semibold mt-1.5"><span className="text-sky-700">{nkCount}</span> <span className="text-[var(--muted)] text-sm">/</span> <span className="text-violet-700">{rrCount}</span></div><div className="text-[10px] text-[var(--muted)] mt-1">paid salaries deduct from partner payable</div></div>
        </div>

        <div className="flex flex-wrap gap-2 items-center" data-testid="people-filter-bar">
          <div className="w-56"><Input placeholder="Search name / PAN / GSTIN / account…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="people-search" /></div>
          <div className="w-40"><Select testid="people-role-filter" placeholder="All roles" options={[{ value: "employee", label: "Employees" }, { value: "vendor", label: "Vendors" }]} value={roleFilter} onChange={setRoleFilter} /></div>
          <div className="w-40"><Select testid="people-loc-filter" placeholder="All states" options={locOptions.map((c) => ({ value: c, label: STATE_NAME_BY_CODE[c] ? `${c} · ${STATE_NAME_BY_CODE[c]}` : c }))} value={locFilter} onChange={setLocFilter} /></div>
          <div className="w-32"><Select testid="people-tag-filter" placeholder="All tags" options={[{ value: "NK", label: "NK" }, { value: "RR", label: "RR" }, { value: "untagged", label: "Untagged" }]} value={tagFilter} onChange={setTagFilter} /></div>
          {hasFilters && <Btn variant="ghost" onClick={() => { setQ(""); setRoleFilter(""); setLocFilter(""); setTagFilter(""); }}>Clear filters</Btn>}
        </div>

        <Section title={`${filtered.length} person${filtered.length === 1 ? "" : "s"}${hasFilters ? " · filtered" : ""}`} testid="people-table">
          <div className="overflow-auto max-h-[640px]">
            <div className="overflow-x-auto"><table className="table-data w-full">
              <thead><tr>
                <SortHeader k="name">Name</SortHeader>
                <SortHeader k="role">Role</SortHeader>
                <SortHeader k="tag">Tag</SortHeader>
                <SortHeader k="for_from">For / From</SortHeader>
                <th>GST/PAN</th>
                <SortHeader k="salary_per_month" align="right">CTC (Annual)</SortHeader>
                <SortHeader k="fy_paid" align="right">Paid · {rows[0]?.fy_label || "FY"}</SortHeader>
                <SortHeader k="fy_pending" align="right">Pending</SortHeader>
                <th>Account #</th><th>IFSC</th>
                <SortHeader k="location">State</SortHeader>
                <th></th>
              </tr></thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id} data-testid={`people-row-${r.id}`}>
                    <td className="font-medium"><button onClick={() => openDrawer(r)} className="text-left hover:text-[var(--accent)]" data-testid={`open-salary-${r.id}`}>{r.name}</button></td>
                    <td><Badge tone={r.role === "vendor" ? "accent" : "muted"}>{r.role}</Badge></td>
                    <td>{r.tag === "NK" ? <span className="inline-flex items-center px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider border rounded-sm bg-sky-50 text-sky-800 border-sky-200">NK</span> : r.tag === "RR" ? <span className="inline-flex items-center px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider border rounded-sm bg-violet-50 text-violet-800 border-violet-200">RR</span> : <span className="text-[var(--muted)] text-[11px]">—</span>}</td>
                    <td className="text-[12px]">{r.for_from || "—"}</td>
                    <td className="num text-[12px]">
                      {r.role === "vendor" ? (r.gstin || <span className="text-[var(--muted)]">—</span>) : (r.pan || <span className="text-[var(--muted)]">—</span>)}
                    </td>
                    <td className="text-right num" data-testid={`p-ctc-${r.id}`}>{r.salary_per_month ? fmtINR(r.salary_per_month, 0) : <span className="text-[var(--muted)]">—</span>}</td>
                    <td className="text-right num text-[var(--positive)]" data-testid={`p-paid-${r.id}`}>{r.salary_per_month ? fmtINR(r.fy_paid || 0, 0) : <span className="text-[var(--muted)]">—</span>}</td>
                    <td className={`text-right num font-semibold ${(r.fy_pending || 0) > 0 ? "text-[var(--negative)]" : "text-[var(--muted)]"}`} data-testid={`p-pending-${r.id}`}>
                      {r.salary_per_month ? fmtINR(r.fy_pending || 0, 0) : <span className="text-[var(--muted)]">—</span>}
                    </td>
                    <td className="num text-[12px]">{r.account_number || "—"}</td>
                    <td className="num text-[12px]">{r.ifsc || "—"}</td>
                    <td><StatePill value={r.location} /></td>
                    <td className="text-right">
                      <button onClick={() => openDrawer(r)} className="p-1.5 hover:bg-[var(--surface-2)] rounded-sm" title="Salary history" data-testid={`salary-icon-${r.id}`}><Wallet size={13} /></button>
                      <button onClick={() => window.open(apiUrl(`/export/salary-ledger?person_id=${r.id}`), "_blank")} className="p-1.5 hover:bg-[var(--surface-2)] rounded-sm" title="Download this person's ledger CSV" data-testid={`dl-ledger-${r.id}`}><Download size={13} /></button>
                      <button onClick={() => openEdit(r)} className="p-1.5 hover:bg-[var(--surface-2)] rounded-sm"><Pencil size={13} /></button>
                      <button onClick={() => remove(r.id)} className="p-1.5 hover:bg-red-50 text-[var(--negative)] rounded-sm"><Trash2 size={13} /></button>
                    </td>
                  </tr>
                ))}
                {!filtered.length && <tr><td colSpan={12} className="text-center text-[var(--muted)] py-6">No people match.</td></tr>}
              </tbody>
              {filtered.length > 0 && (
                <tfoot className="bg-[var(--surface-2)] font-semibold text-sm">
                  <tr data-testid="people-totals-row">
                    <td colSpan={5} className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">
                      Totals · {filtered.length} person{filtered.length === 1 ? "" : "s"}
                    </td>
                    <td className="text-right num" data-testid="people-total-ctc">
                      {fmtINR(filtered.reduce((s, r) => s + Number(r.salary_per_month || 0), 0), 0)}
                    </td>
                    <td className="text-right num text-[var(--positive)]" data-testid="people-total-paid">
                      {fmtINR(filtered.reduce((s, r) => s + Number(r.fy_paid || 0), 0), 0)}
                    </td>
                    <td className="text-right num text-[var(--negative)]" data-testid="people-total-pending">
                      {fmtINR(filtered.reduce((s, r) => s + Number(r.fy_pending || 0), 0), 0)}
                    </td>
                    <td colSpan={4}></td>
                  </tr>
                </tfoot>
              )}
            </table></div>
          </div>
        </Section>
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={editId ? "Edit Person" : "New Person"} testid="people-modal">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Name</label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          <div><label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Role</label>
            <Select options={[{ value: "employee", label: "Employee" }, { value: "vendor", label: "Vendor" }]} value={form.role} onChange={(v) => setForm({ ...form, role: v })} /></div>
          <div><label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider flex items-center gap-1.5">Tag <span className="relative inline-flex group/tip"><span className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full text-[var(--muted)] hover:text-[var(--accent)] cursor-help">ⓘ</span><span role="tooltip" className="pointer-events-none absolute left-0 top-full mt-2 z-30 w-[260px] px-3 py-2 bg-[var(--ink)] text-white text-[11px] font-normal normal-case tracking-normal leading-snug rounded-sm shadow-lg opacity-0 group-hover/tip:opacity-100 transition-opacity duration-150">Paid salaries to NK/RR-tagged people are auto-deducted from that partner's monthly payable in Monthly Accounting (treated as an advance). Migration entries with notes containing "Migrated" are skipped.</span></span></label>
            <Select placeholder="Untagged" testid="form-tag" options={[{ value: "NK", label: "NK" }, { value: "RR", label: "RR" }]} value={form.tag || ""} onChange={(v) => setForm({ ...form, tag: v })} /></div>
          <div className="col-span-2"><label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">For / From</label>
            <ComboSelect
              testid="form-for-from"
              options={forFromOpts}
              value={form.for_from}
              onChange={(v) => setForm({ ...form, for_from: v })}
              placeholder="— select —"
            />
            <div className="text-[10.5px] text-[var(--muted)] mt-1">Pick from existing values, or use "+ Add new…" to define a new one (e.g. "Bizassist - Rahul").</div></div>
          <div><label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">PAN</label>
            <Input value={form.pan} onChange={(e) => setForm({ ...form, pan: e.target.value })} /></div>
          {form.role === "vendor" && (
            <div><label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">GSTIN</label>
              <Input value={form.gstin} onChange={(e) => setForm({ ...form, gstin: e.target.value })} /></div>
          )}
          <div><label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">CTC (Annual ₹)</label>
            <Input type="number" data-testid="form-ctc" value={form.salary_per_month} onChange={(e) => setForm({ ...form, salary_per_month: e.target.value })} placeholder="e.g. 1200000 for 1L/month × 12" />
            <div className="text-[10.5px] text-[var(--muted)] mt-1">Annual total (CTC). Salary entries inherit it (read-only) — only the per-month <i>paid</i> amount is editable. Default monthly run-rate = CTC / 12.</div></div>
          <div><label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Account #</label>
            <Input value={form.account_number} onChange={(e) => setForm({ ...form, account_number: e.target.value })} /></div>
          <div><label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">IFSC</label>
            <Input value={form.ifsc} onChange={(e) => setForm({ ...form, ifsc: e.target.value })} /></div>
          <div><label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">State</label>
            <Select testid="form-state" placeholder="Select state…" options={STATE_OPTIONS.slice(1)} value={normaliseLocation(form.location) || ""} onChange={(v) => setForm({ ...form, location: v })} />
            <div className="text-[10.5px] text-[var(--muted)] mt-1">Pick from the 28 states + 8 UTs. The 2-letter code shows on the People list.</div></div>
          <div className="col-span-2"><label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Comments</label>
            <Input value={form.comments} onChange={(e) => setForm({ ...form, comments: e.target.value })} /></div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <Btn variant="ghost" onClick={() => setOpen(false)}>Cancel</Btn>
          <Btn variant="accent" onClick={save}>{editId ? "Save" : "Create"}</Btn>
        </div>
      </Modal>

      {/* Salary drawer */}
      {drawerPerson && (
        <>
          <div className="fixed inset-0 bg-black/40 z-40" onClick={closeDrawer} />
          <aside className="fixed inset-y-0 right-0 w-[640px] max-w-full bg-[var(--surface)] z-50 shadow-2xl overflow-y-auto" data-testid="salary-drawer">
            <div className="p-6 border-b border-[var(--line)] flex items-start justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--muted)] font-semibold">Salary history</div>
                <div className="text-xl font-semibold mt-1">{drawerPerson.name}</div>
                <div className="text-[11px] text-[var(--muted)] mt-0.5">{drawerPerson.for_from || "—"} · {drawerPerson.role} · PAN {drawerPerson.pan || "—"}</div>
              </div>
              <button onClick={closeDrawer} className="p-1.5 hover:bg-[var(--surface-2)] rounded-sm"><X size={16} /></button>
            </div>
            <div className="p-6 space-y-5">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--muted)] font-semibold mb-2">By Financial Year</div>
                <div className="overflow-x-auto"><table className="table-data w-full">
                  <thead><tr><th>FY</th><th className="text-right">CTC</th><th className="text-right">Paid</th><th className="text-right">Pending</th><th className="text-right">Entries</th></tr></thead>
                  <tbody>
                    {ledger.rollup.map((r) => (
                      <tr key={r.fy}>
                        <td className="font-medium">{r.fy}</td>
                        <td className="text-right num">{fmtINR(r.due, 0)}</td>
                        <td className="text-right num text-[var(--positive)]">{fmtINR(r.paid, 0)}</td>
                        <td className={`text-right num font-semibold ${r.pending > 0 ? "text-[var(--negative)]" : "text-[var(--positive)]"}`}>{fmtINR(r.pending, 0)}</td>
                        <td className="text-right num text-[12px] text-[var(--muted)]">{r.rows}</td>
                      </tr>
                    ))}
                    {!ledger.rollup.length && <tr><td colSpan={5} className="text-center text-[var(--muted)] py-4">No entries yet.</td></tr>}
                  </tbody>
                </table></div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--muted)] font-semibold">Entries</div>
                  <Btn variant="accent" testid="add-salary-entry" onClick={openNewLedger}><Plus size={12} /> Add entry</Btn>
                </div>
                <div className="overflow-x-auto"><table className="table-data w-full">
                  <thead><tr><th>Month</th><th>FY</th><th className="text-right">CTC</th><th className="text-right">Paid</th><th>Status</th><th>Notes</th><th></th></tr></thead>
                  <tbody>
                    {ledger.rows.map((r) => {
                      const status = (r.approval_status || "approved").toLowerCase();
                      const tone =
                        status === "pending" ? "bg-amber-50 text-amber-800 border-amber-200" :
                        status === "rejected" ? "bg-red-50 text-red-800 border-red-200" :
                        "bg-emerald-50 text-emerald-800 border-emerald-200";
                      const label = status === "pending" ? "Pending" : status === "rejected" ? "Rejected" : "Approved";
                      return (
                        <tr key={r.id}>
                          <td className="num">{fmtPeriod(r.month)}</td>
                          <td className="text-[12px] text-[var(--muted)]">{r.fy}</td>
                          <td className="text-right num">{fmtINR(r.amount_due, 0)}</td>
                          <td className="text-right num text-[var(--positive)]">{fmtINR(r.amount_paid, 0)}</td>
                          <td><span className={`inline-flex items-center px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider border rounded-sm ${tone}`}>{label}</span></td>
                          <td className="text-[11px] text-[var(--muted)] max-w-xs truncate">{r.notes || "—"}</td>
                          <td className="text-right">
                            <button onClick={() => editLedger(r)} className="p-1 hover:bg-[var(--surface-2)] rounded-sm"><Pencil size={12} /></button>
                            <button onClick={() => removeLedger(r.id)} className="p-1 hover:bg-red-50 text-[var(--negative)] rounded-sm"><Trash2 size={12} /></button>
                          </td>
                        </tr>
                      );
                    })}
                    {!ledger.rows.length && <tr><td colSpan={7} className="text-center text-[var(--muted)] py-4">No entries.</td></tr>}
                  </tbody>
                </table></div>
              </div>
            </div>
          </aside>
        </>
      )}

      <Modal open={ledgerOpen} onClose={() => setLedgerOpen(false)} title={ledgerEditId ? "Edit Salary Entry" : "Add Salary Entry"} testid="ledger-modal">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Month</label>
            <MonthPicker value={ledgerForm.month} onChange={(v) => setLedgerForm({ ...ledgerForm, month: v })} testid="ledger-form-month" /></div>
          <div>
            <label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">CTC Annual (locked) (₹)</label>
            <Input type="number" value={ledgerForm.amount_due} disabled readOnly data-testid="ledger-form-due" className="bg-[var(--surface-2)] cursor-not-allowed text-[var(--muted)]" />
            <div className="text-[10.5px] text-[var(--muted)] mt-1">Set on the person profile (annual). The default Paid is the monthly run-rate (CTC ÷ 12) — edit below if needed.</div>
          </div>
          <div>
            <label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Amount Paid this month (₹)</label>
            <Input type="number" value={ledgerForm.amount_paid} onChange={(e) => setLedgerForm({ ...ledgerForm, amount_paid: e.target.value })} data-testid="ledger-form-paid" />
            <div className="text-[10.5px] text-[var(--muted)] mt-1">Multiple entries per person/month are allowed (e.g. split payment).</div>
          </div>
          <div className="sm:col-span-2"><label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Notes</label>
            <Input value={ledgerForm.notes} onChange={(e) => setLedgerForm({ ...ledgerForm, notes: e.target.value })} /></div>
          {drawerPerson && (drawerPerson.tag === "NK" || drawerPerson.tag === "RR") && !ledgerEditId && Number(ledgerForm.amount_paid || 0) > 0 && (
            <div className="sm:col-span-2 p-2.5 rounded-sm border border-amber-200 bg-amber-50 text-[11.5px] text-amber-900" data-testid="approval-required-banner">
              ⚠ <b>{drawerPerson.tag} approval required</b> — this entry will start as <i>pending</i> and won't deduct from {drawerPerson.tag}'s monthly payable until it's approved on the Monthly Accounting page. An approval email will be sent to the {drawerPerson.tag} email if one is configured in Settings.
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <Btn variant="ghost" onClick={() => setLedgerOpen(false)}>Cancel</Btn>
          <Btn variant="accent" onClick={saveLedger}>{ledgerEditId ? "Save" : "Add"}</Btn>
        </div>
      </Modal>

      {/* Bulk salary run modal */}
      <Modal open={bulkOpen} onClose={() => setBulkOpen(false)} title="Run Monthly Salaries" size="xl" testid="bulk-salary-modal">
        <div className="space-y-4">
          <div className="grid grid-cols-[220px,auto] gap-3 items-end">
            <div>
              <label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Month</label>
              <MonthPicker value={bulkMonth} onChange={setBulkMonth} testid="bulk-salary-month" />
            </div>
            <div className="flex gap-2 flex-wrap">
              <Btn variant="ghost" testid="bulk-copy-last" onClick={copyLastMonth} disabled={!bulkMonth}>Copy last month's amounts</Btn>
              <Btn variant="ghost" testid="bulk-reset" onClick={resetToSalary}>Reset to monthly salary</Btn>
            </div>
          </div>
          <div className="text-[11.5px] text-[var(--muted)] bg-[var(--surface-2)] border border-[var(--line)] p-2.5 rounded-sm">
            Creates one ledger entry per selected person for <b>{bulkMonth ? fmtPeriod(bulkMonth) : "the chosen month"}</b>. Multiple entries per person/month are allowed (split payments). CTC (Annual) is locked to the profile; <b>Pending</b> = CTC − Paid (FY-to-date) for quick reference; <b>Paid</b> defaults to CTC ÷ 12 — edit if the actual transfer differed.
            <span className="block mt-1 text-amber-700">⚠ NK/RR-tagged rows with Paid &gt; 0 start as <b>pending</b> and trigger an approval email — the deduction kicks in once the entry is approved on Monthly Accounting.</span>
          </div>
          <div className="flex items-center justify-between text-[11.5px] px-1 flex-wrap gap-2">
            <div className="flex items-center gap-1.5 flex-wrap">
              <button type="button" className="underline text-[var(--accent)]" onClick={() => toggleAllBulk(true)} data-testid="bulk-select-all">Select all</button>
              <span className="text-[var(--muted)]">·</span>
              <button type="button" className="underline text-[var(--muted)]" onClick={() => toggleAllBulk(false)} data-testid="bulk-clear-all">Clear all</button>
              <span className="text-[var(--muted)] mx-1">|</span>
              <span className="text-[10.5px] uppercase tracking-wider text-[var(--muted)] font-semibold">By tag:</span>
              <button type="button" onClick={() => selectByTag("NK")} className="px-1.5 py-0.5 rounded-sm border border-sky-200 bg-sky-50 text-sky-800 hover:bg-sky-100 text-[10.5px] font-bold uppercase tracking-wider" data-testid="bulk-select-nk">NK only</button>
              <button type="button" onClick={() => selectByTag("RR")} className="px-1.5 py-0.5 rounded-sm border border-violet-200 bg-violet-50 text-violet-800 hover:bg-violet-100 text-[10.5px] font-bold uppercase tracking-wider" data-testid="bulk-select-rr">RR only</button>
              <button type="button" onClick={() => selectByTag(null)} className="px-1.5 py-0.5 rounded-sm border border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100 text-[10.5px] font-semibold uppercase tracking-wider" data-testid="bulk-select-untagged">Untagged</button>
            </div>
            <div className="text-[var(--muted)]">{bulkSelectedCount} of {bulkRows.length} selected · total salary: <span className="num font-semibold text-[var(--ink)]">₹ {fmtINR(bulkTotalDue, 0)}</span> · total paid: <span className="num font-semibold text-[var(--positive)]">₹ {fmtINR(bulkTotalPaid, 0)}</span></div>
          </div>
          <div className="border border-[var(--line)] rounded-sm max-h-[400px] overflow-auto">
            <div className="overflow-x-auto"><table className="table-data w-full">
              <thead className="sticky top-0 bg-[var(--surface)] z-10">
                <tr>
                  <th className="w-8"></th>
                  <th>Name</th>
                  <th>For / From</th>
                  <th className="text-right w-[140px]">CTC (Annual ₹)</th>
                  <th className="text-right w-[140px]">Pending (FY)</th>
                  <th className="text-right w-[170px]">Paying (₹)</th>
                  <th className="min-w-[180px]">Notes</th>
                </tr>
              </thead>
              <tbody>
                {bulkRows.map((r) => (
                  <tr key={r.person_id} className={r.selected ? "" : "opacity-50"} data-testid={`bulk-row-${r.person_id}`}>
                    <td><input type="checkbox" checked={r.selected} onChange={(e) => updateBulkRow(r.person_id, "selected", e.target.checked)} className="h-4 w-4 accent-[var(--accent)]" data-testid={`bulk-chk-${r.person_id}`} /></td>
                    <td className="font-medium text-[12.5px]">
                      <div className="flex items-center gap-1.5">
                        {r.name}
                        {r.tag === "NK" && <span className="inline-flex items-center px-1 py-0 text-[9.5px] font-bold uppercase tracking-wider border rounded-sm bg-sky-50 text-sky-800 border-sky-200" title="NK approval required when paid > 0">NK</span>}
                        {r.tag === "RR" && <span className="inline-flex items-center px-1 py-0 text-[9.5px] font-bold uppercase tracking-wider border rounded-sm bg-violet-50 text-violet-800 border-violet-200" title="RR approval required when paid > 0">RR</span>}
                      </div>
                    </td>
                    <td className="text-[11px] text-[var(--muted)]">{r.for_from || "—"}</td>
                    <td className="text-right num text-[12.5px] font-semibold tabular-nums" data-testid={`bulk-due-${r.person_id}`} title={`Annual CTC: ₹${r.ctc_annual.toLocaleString("en-IN")} · Monthly run-rate: ≈₹${r.ctc_monthly.toLocaleString("en-IN")}/mo`}>
                      ₹ {r.ctc_annual.toLocaleString("en-IN")}
                    </td>
                    <td className={`text-right num text-[12.5px] font-semibold tabular-nums ${r.fy_pending > 0 ? "text-[var(--negative)]" : "text-[var(--muted)]"}`} data-testid={`bulk-pending-${r.person_id}`} title={`${r.fy_label} pending = CTC − Paid (FY-to-date)`}>
                      {r.fy_pending > 0 ? `₹ ${r.fy_pending.toLocaleString("en-IN")}` : "—"}
                    </td>
                    <td>
                      <Input type="number" value={r.amount_paid} onChange={(e) => updateBulkRow(r.person_id, "amount_paid", e.target.value)} className="text-right num h-8" data-testid={`bulk-paid-${r.person_id}`} />
                    </td>
                    <td>
                      <Input value={r.notes} onChange={(e) => updateBulkRow(r.person_id, "notes", e.target.value)} placeholder="Optional" className="h-8 text-[12px]" />
                    </td>
                  </tr>
                ))}
                {!bulkRows.length && <tr><td colSpan={7} className="text-center text-[var(--muted)] py-6">No one has a CTC set. Edit a person and add their CTC first.</td></tr>}
              </tbody>
            </table></div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <Btn variant="ghost" onClick={() => setBulkOpen(false)}>Cancel</Btn>
          <Btn variant="accent" testid="bulk-save" onClick={saveBulk} disabled={bulkBusy || !bulkSelectedCount || !bulkMonth}>{bulkBusy ? "Running…" : `Run for ${bulkSelectedCount} ${bulkSelectedCount === 1 ? "person" : "people"}`}</Btn>
        </div>
      </Modal>
    </div>
  );
}
